import { useEffect, useRef, useState } from 'react'
import { Search, SlidersHorizontal, X } from 'lucide-react'
import { cn } from '@/utils/cn'
import { CloserToolbarSelect } from '@/components/CloserLeads/CloserToolbarSelect'
import { PAGE_TOOLBAR, SegmentTabs } from '@/components/ui/page-toolbar'
import { FinanceQueueFiltersPanel } from '@/components/Financeiro/FinanceQueueFiltersPanel'
import { FINANCE_QUEUE_LIFECYCLE_TABS } from '@/components/Financeiro/financeQueueLifecycle'
import { FINANCE_AREA_META, type FinanceAreaKey } from '@/components/Financeiro/solicitacoes/financeSolicitacoesConfig'
import type { FinanceAssignee } from '@/services/financeService'
import type { QueueLifecycleFilter, QueueSlaFilter } from '@/hooks/useFinanceiroPage'

const searchInputClass =
  'min-h-[44px] w-full min-w-0 border-0 bg-transparent py-3 pl-10 pr-10 text-sm font-medium text-text-primary placeholder:text-text-secondary outline-none transition-colors focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/25'

const AREA_OPTIONS = Object.entries(FINANCE_AREA_META).map(([key, meta]) => ({
  value: key as FinanceAreaKey,
  label: meta.label,
}))

type Props = {
  queueLifecycle: QueueLifecycleFilter
  setQueueLifecycle: (v: QueueLifecycleFilter) => void
  queueSearch: string
  setQueueSearch: (v: string) => void
  queueStatus: string
  setQueueStatus: (v: string) => void
  queueArea: string
  setQueueArea: (v: string) => void
  queueType: string
  setQueueType: (v: string) => void
  queueSla: QueueSlaFilter
  setQueueSla: (v: QueueSlaFilter) => void
  queueAssignee: string
  setQueueAssignee: (v: string) => void
  queueUnassigned: boolean
  setQueueUnassigned: (v: boolean) => void
  assignees: FinanceAssignee[]
  typeOptions: { key: string; label: string }[]
}

export function FinanceQueueFilters({
  queueLifecycle,
  setQueueLifecycle,
  queueSearch,
  setQueueSearch,
  queueStatus,
  setQueueStatus,
  queueArea,
  setQueueArea,
  queueType,
  setQueueType,
  queueSla,
  setQueueSla,
  queueAssignee,
  setQueueAssignee,
  queueUnassigned,
  setQueueUnassigned,
  assignees,
  typeOptions,
}: Props) {
  const [filtersOpen, setFiltersOpen] = useState(false)
  const filtersRootRef = useRef<HTMLDivElement>(null)

  const advancedFilterCount = [
    Boolean(queueStatus),
    Boolean(queueType),
    Boolean(queueSla),
    queueUnassigned || Boolean(queueAssignee),
  ].filter(Boolean).length

  const hasActiveFilter =
    queueSearch.trim().length > 0 ||
    Boolean(queueStatus) ||
    Boolean(queueArea) ||
    Boolean(queueType) ||
    Boolean(queueSla) ||
    queueUnassigned ||
    Boolean(queueAssignee)

  const clearAllFilters = () => {
    setQueueLifecycle('ativos')
    setQueueSearch('')
    setQueueStatus('')
    setQueueArea('')
    setQueueType('')
    setQueueSla('')
    setQueueAssignee('')
    setQueueUnassigned(false)
    setFiltersOpen(false)
  }

  const areaOptions = [{ value: '', label: 'Todas as áreas' }, ...AREA_OPTIONS]

  const filtersHighlighted = filtersOpen || advancedFilterCount > 0

  useEffect(() => {
    if (!filtersOpen) return

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFiltersOpen(false)
    }

    const onPointerDown = (e: MouseEvent) => {
      if (filtersRootRef.current && !filtersRootRef.current.contains(e.target as Node)) {
        setFiltersOpen(false)
      }
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [filtersOpen])

  return (
    <div className='space-y-3'>
      <SegmentTabs
        value={queueLifecycle}
        onChange={(value) => {
          setQueueLifecycle(value)
          setQueueStatus('')
        }}
        tabs={[...FINANCE_QUEUE_LIFECYCLE_TABS]}
        ariaLabel='Situação dos chamados'
        className='w-full overflow-x-auto'
      />

      <div className='glass-card p-4'>
      <div className='grid w-full grid-cols-1 items-center gap-3 md:grid-cols-[minmax(0,1fr)_auto]'>
        <div className='closer-toolbar-field-shell relative min-w-0 overflow-hidden rounded-xl'>
          <Search
            size={16}
            className='pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-text-muted'
          />
          <input
            type='text'
            placeholder='Buscar protocolo, cliente ou tipo...'
            value={queueSearch}
            onChange={(e) => setQueueSearch(e.target.value)}
            aria-label='Buscar chamados'
            className={searchInputClass}
          />
          {queueSearch && (
            <button
              type='button'
              onClick={() => setQueueSearch('')}
              className='absolute right-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-text-muted transition-colors hover:text-red-500 dark:hover:text-red-400'
              aria-label='Limpar busca'
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div className='flex shrink-0 flex-wrap items-center justify-end gap-3 md:justify-self-end'>
          <CloserToolbarSelect
            value={queueArea}
            onChange={setQueueArea}
            options={areaOptions}
            aria-label='Filtrar por área'
          />

          <div ref={filtersRootRef} className='relative shrink-0'>
            <button
              type='button'
              onClick={() => setFiltersOpen((open) => !open)}
              aria-expanded={filtersOpen}
              aria-haspopup='dialog'
              className={cn(
                'closer-toolbar-field-shell flex min-h-[44px] shrink-0 items-center gap-2 rounded-xl px-4 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent/25',
                filtersHighlighted
                  ? 'text-accent'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              <SlidersHorizontal size={15} aria-hidden />
              <span>Filtros</span>
              {advancedFilterCount > 0 && (
                <span className={PAGE_TOOLBAR.filterBadge}>{advancedFilterCount}</span>
              )}
            </button>

            <FinanceQueueFiltersPanel
              open={filtersOpen}
              onClose={() => setFiltersOpen(false)}
              onClearAll={clearAllFilters}
              queueStatus={queueStatus}
              setQueueStatus={setQueueStatus}
              queueType={queueType}
              setQueueType={setQueueType}
              queueSla={queueSla}
              setQueueSla={setQueueSla}
              queueAssignee={queueAssignee}
              setQueueAssignee={setQueueAssignee}
              queueUnassigned={queueUnassigned}
              setQueueUnassigned={setQueueUnassigned}
              assignees={assignees}
              typeOptions={typeOptions}
            />
          </div>

          {hasActiveFilter && (
            <button
              type='button'
              onClick={clearAllFilters}
              className='flex h-10 shrink-0 items-center gap-1.5 rounded-lg border border-red-500/20 bg-red-500/10 px-3 text-xs font-medium text-red-500 transition-all hover:bg-red-500/20 dark:text-red-400'
            >
              <X size={14} /> Limpar
            </button>
          )}
        </div>
      </div>
      </div>
    </div>
  )
}
